/**
 * La Pause annoncée — **avenant A-2** (`docs/AVENANTS.md`).
 *
 * L'idée : si le joueur **annonce** sa pause avant de disparaître, le silence ne le punit
 * plus, et son retour fait un événement. Silence non annoncé = punition inchangée.
 *
 * Ce module porte l'**action**, la **découverte** semi-cachée, le **bonus de retour** et les
 * **garde-fous anti-abus** (points 3, 4, 5, 5b). Le gel du silence, la récupération SM ×1,5
 * et la rente pendant la pause sont déjà dans `sleep.ts` (points 1, 2, 6, tâche M1.11).
 *
 * ── Les trois transitoires ⟦A-2⟧, et ce qu'ils sont devenus ⟦SPEC-FIL §2.5, M3.21⟧ ──
 *
 * **Arbitrage Hype tranché par Mathieu (option 2)** : les effets chiffrés « en Hype » de
 * l'avenant avaient été re-mappés sur les jauges qui existaient — bonus de retour = Fidélité +3
 * et image +3, pause trop courte = image −3, abus = les malus image/Fidélité suffisent.
 *
 * La Hype existe maintenant, et SPEC-FIL §2.5 rend les trois effets à leur jauge :
 *
 * | Situation | Transitoire ⟦A-2⟧ | Effet définitif ⟦§2.5⟧ |
 * |---|---|---|
 * | Retour de pause bonifiée | Fidélité +3 **et** image +3 | **Hype +10** |
 * | Pause annoncée trop courte | image −3 | **Hype −5** |
 * | Pause abusive | (les malus image/Fidélité restent) | **Hype −10 en plus** |
 *
 * 🔑 **Les deux ne sont JAMAIS actifs en même temps** — c'est toute la règle, et elle tient en
 * un `if` : `filActif(content)` choisit. Sinon l'effet serait compté deux fois, et le
 * déséquilibre serait invisible.
 *
 * ⚠️ **Pourquoi le transitoire n'est pas simplement SUPPRIMÉ**, alors que §2.5 écrit « les
 * compensations disparaissent » : `meta.fil_actif` vaut encore `false` (l'écran est M3.23), et
 * `gagnerHype` ne pose rien quand le Fil est éteint. Supprimer sèchement laisserait donc un
 * retour de pause **sans aucun effet** — un nerf que personne n'a décidé, invisible sur une
 * jauge que personne ne regarde, et qui casserait au passage la preuve d'état neutre ⟦A-10⟧
 * (une campagne éteinte doit rester identique au caractère près, et les profils de
 * `simulate.ts` annoncent des pauses). Le transitoire ne survit donc **que** dans la branche
 * éteinte : le jour de l'allumage, il s'efface tout seul, et c'est un caractère à changer —
 * exactement le contrat d'⟦A-15⟧, « empiler n'a de sens que si dépiler est une ligne ».
 */
import { H_JOUR } from "./clock.js";
import { CAPACITE_COEUR_SUR_VOUS, capaciteDeLArbre, filActif, gagnerHype, NOEUD_FIGURE_PUBLIQUE, } from "./fil.js";
import { appliquerTraitJauge } from "./traits.js";
// ─────────────────────────────────────────────────────────────────────────────
// Constantes de l'avenant
// ─────────────────────────────────────────────────────────────────────────────
/** Coût de l'annonce (point 4) : 1 h simulée, 4 énergie. */
export const ANNONCE_HEURES = 1;
export const ANNONCE_ENERGIE = -4;
/** La SM sous ce seuil (zone rouge) révèle l'action — pédagogie par le besoin. */
export const SEUIL_DECOUVERTE_SM = 30;
/**
 * La capacité qui débloque l'action d'office (A-2, « Découverte » route 2) : **Cœur sur vous**.
 *
 * ⟦A-82⟧ Ce module lisait un id de nœud (`"coeur_sur_vous"`) qui n'a jamais existé dans
 * `content/skills.json` — la route « par l'arbre » était inerte depuis A-2. L'arbre étant FERMÉ
 * ⟦A-21⟧, *Cœur sur vous* est devenu une **capacité** portée par un nœud existant (*Le live*,
 * branche Charisme), et c'est elle qu'on lit ici — la même que `fil.ts` pour le +50 % des
 * réponses `coeur`. La route « pédagogie par le besoin » (SM < 30) reste la première.
 */
export const CAPACITE_DECOUVERTE = CAPACITE_COEUR_SUR_VOUS;
/**
 * Ce que le nœud *Figure publique* majore sur le bonus de retour (A-2 point 6).
 *
 * Le nœud est lu **par son id**, à contre-courant de la règle « contenu = données » — et c'est
 * assumé : l'enum `MultiplierTarget` de SPEC-CONTENU §2.1 est **fermé** et n'a pas de cible
 * « retour_pause », donc le contenu ne peut PAS exprimer cet effet aujourd'hui. Lui ajouter
 * une cible est une modification du schéma de contenu, qui appartient à la passe M3.
 *
 * ⟦M3.21⟧ **L'id, lui, n'est déclaré qu'une fois — dans `fil.ts`**, qui le lit déjà pour la
 * décroissance de Hype ⟦A-57⟧. Deux constantes pour le même nœud, c'est deux endroits où une
 * faute de frappe rend une mécanique silencieusement inerte : c'est le mot de `fins.ts`, qui
 * l'importait ici pour cette raison et l'importe désormais de là-bas.
 */
export const MULT_FIGURE_PUBLIQUE = 1.2;
/** Durée minimale d'une pause pour valoir un bonus (point 5b) : 5 jours simulés. */
export const H_PAUSE_MIN_BONUS = 5 * H_JOUR; // 80 h
/** Paliers du bonus de retour (point 3), du plus long au plus court. */
export const PALIERS_RETOUR = [
    { jours: 15, mult: 1.5 }, // cap
    { jours: 10, mult: 1.35 },
    { jours: 5, mult: 1.2 },
];
/** Une seule pause **bonifiée** par 90 jours simulés (point 5a). */
export const H_FENETRE_BONIFIEE = 90 * H_JOUR; // 1 440 h
/** Au-delà de ce nombre de pauses annoncées sur la run, la régularité se paie (point 5d). */
export const PAUSES_AVANT_PENALITE = 6;
/** Chaque pause au-delà du plafond : bonus ×0,8 cumulatif et cap de momentum −0,05. */
export const PENALITE_BONUS_CUMUL = 0.8;
export const PENALITE_CAP_MOMENTUM = 0.05;
/**
 * Bonus de retour, **transitoire ⟦A-2⟧** : n'a lieu que tant que `meta.fil_actif` est `false`.
 * Fil allumé, c'est `RETOUR_HYPE` qui s'applique — et lui seul (voir l'en-tête).
 */
export const RETOUR_FIDELITE = 3;
export const RETOUR_IMAGE = 3;
/** ⟦SPEC-FIL §2.5⟧ L'effet **définitif** du retour de pause bonifiée. ✎ */
export const RETOUR_HYPE = 10;
/** « Il est revenu en forme » (point 3) : Fidélité +5 si la Qualité suit. */
export const RETOUR_FIDELITE_EN_FORME = 5;
export const SEUIL_QUALITE_EN_FORME = 1.2;
/** Pause trop courte : pas de bonus, et des moqueries (point 5b, **transitoire ⟦A-2⟧**). */
export const PAUSE_COURTE_IMAGE = -3;
/** ⟦SPEC-FIL §2.5⟧ L'effet **définitif** d'une pause annoncée trop courte. ✎ */
export const PAUSE_COURTE_HYPE = -5;
/** Pause **abusive** — hors fenêtre des 90 j (point 5b). Ces deux-là **restent** ⟦§2.5⟧. */
export const ABUS_IMAGE = -10;
export const ABUS_FIDELITE = -5;
/** ⟦SPEC-FIL §2.5⟧ Ce que l'abus coûte **en plus** à la Hype, Fil allumé. ✎ */
export const ABUS_HYPE = -10;
/** À la 3e pause abusive : bad buzz dédié « Il se moque de nous » (contenu, M3). */
export const ABUS_SEUIL_BAD_BUZZ = 3;
/**
 * ⟦A-2 point 5b · A-104⟧ L'id du contenu que la 3e pause abusive programme — même entorse
 * assumée que `JALON_EXCUSES` (`jalons.ts`) ou `EVENEMENT_PASSATION` (`events.ts`) : un id de
 * `content/events.json` lu directement par le moteur, documenté ici pour qu'une faute de
 * frappe des deux côtés ne rende pas la mécanique silencieusement inerte.
 */
export const EVENEMENT_PAUSE_ABUSIVE = "pause_il_se_moque_de_nous";
/** Plafond de momentum de référence (§1.6). */
export const MOMENTUM_PLAFOND_BASE = 1.5;
// ─────────────────────────────────────────────────────────────────────────────
// Lectures
// ─────────────────────────────────────────────────────────────────────────────
/**
 * L'action `annoncer_pause` est-elle découverte ? (route SM < 30, ou la capacité *Cœur sur
 * vous* par l'arbre ⟦A-82⟧). `content` absent ⇒ la route de l'arbre n'est pas lue — c'est le
 * cas des appelants qui n'ont que l'état sous la main ; la validation de l'action, elle, passe
 * le contenu.
 */
export function pauseDecouverte(state, content) {
    if (state.flags.pause_annoncee_decouverte === true)
        return true;
    return content != null && capaciteDeLArbre(state, content, CAPACITE_DECOUVERTE);
}
/** Nombre de pauses annoncées sur la run (0 si le compteur n'existe pas encore). */
export function pausesAnnoncees(state) {
    return state.counters.pauses_annoncees ?? 0;
}
/** Nombre de pauses abusives sur la run. */
export function pausesAbusives(state) {
    return state.counters.pauses_abusives ?? 0;
}
/**
 * Plafond de momentum courant (point 5d) : 1,5 abaissé de 0,05 par pause annoncée au-delà de
 * la 6e. L'irrégularité chronique se paie **structurellement** — pas de circuit du million en
 * enchaînant vingt pauses. Plancher de sécurité à 0,8 (la borne basse du momentum).
 */
export function plafondMomentum(state) {
    const excedent = Math.max(0, pausesAnnoncees(state) - PAUSES_AVANT_PENALITE);
    return Math.max(0.8, MOMENTUM_PLAFOND_BASE - PENALITE_CAP_MOMENTUM * excedent);
}
/**
 * La prochaine pause serait-elle **abusive** ? Oui si une pause a déjà été bonifiée dans les
 * 90 derniers jours simulés (point 5a/5b). Une pause abusive ne protège plus de rien :
 * « l'audience n'y croit plus ».
 */
export function prochainePauseAbusive(state) {
    const derniere = state.counters.last_pause_bonifiee_h;
    if (derniere === null || derniere === undefined)
        return false;
    return state.clock_h - derniere < H_FENETRE_BONIFIEE;
}
/** Durée en jours simulés d'une pause en cours (0 si aucune). */
export function joursDePause(state, instantH = state.clock_h) {
    const pause = state.pause_announced;
    if (!pause)
        return 0;
    return Math.max(0, (instantH - pause.since_h) / H_JOUR);
}
/**
 * Multiplicateur de VuesPic du retour (point 3), garde-fous compris (5c, 5d) et synergie
 * d'arbre (point 6). Rend **1** quand aucun bonus n'est dû — jamais `null`, pour que
 * l'appelant multiplie sans condition.
 */
export function multiplicateurRetour(state, instantH = state.clock_h) {
    const pause = state.pause_announced;
    if (!pause)
        return 1;
    // Une pause abusive ne donne aucun bonus : elle n'a même pas protégé du silence.
    if (!pause.protege)
        return 1;
    const jours = joursDePause(state, instantH);
    const palier = PALIERS_RETOUR.find((p) => jours >= p.jours);
    if (!palier)
        return 1; // pause < 5 jours : pas de bonus (point 5b)
    let mult = palier.mult;
    if (state.skills.includes(NOEUD_FIGURE_PUBLIQUE))
        mult *= MULT_FIGURE_PUBLIQUE;
    // Point 5d : ×0,8 cumulatif par pause au-delà de la 6e.
    const excedent = Math.max(0, pausesAnnoncees(state) - PAUSES_AVANT_PENALITE);
    mult *= Math.pow(PENALITE_BONUS_CUMUL, excedent);
    return mult;
}
// ─────────────────────────────────────────────────────────────────────────────
// Écritures — toutes MUTENT `state`, qui est le clone de travail de la file (§2.3)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Découverte par le besoin : le **premier** passage de la SM sous 30 révèle l'action
 * (A-2, « Découverte » route 1). À appeler au pas 10 de la file, après les clamps.
 *
 * L'événement scripté du Fil (« prends soin de toi, on sera là ») est du **contenu M3** ; ici
 * on émet le `warning` qui le portera, avec de quoi l'afficher.
 */
export function revelerPauseSiZoneRouge(state, events = []) {
    if (state.flags.pause_annoncee_decouverte === true)
        return false;
    if (state.mental >= SEUIL_DECOUVERTE_SM)
        return false;
    state.flags.pause_annoncee_decouverte = true;
    events.push({
        kind: "warning",
        data: { cause: "pause_annoncee_decouverte", mental: state.mental, scripted: "coeur_sur_vous" },
    });
    return true;
}
/**
 * L'action `annoncer_pause` : elle ne fait qu'**inscrire l'intention**. La pause commence au
 * **sommeil suivant** (point 4) — annoncer puis continuer à publier n'ouvre aucune pause.
 */
export function annoncerPause(state, events = [], log = []) {
    state.counters.pause_intention_h = state.clock_h;
    const abusive = prochainePauseAbusive(state);
    events.push({
        kind: "warning",
        data: {
            cause: "pause_annoncee",
            abusive,
            pauses_deja: pausesAnnoncees(state),
            plafond: PAUSES_AVANT_PENALITE,
        },
    });
    log.push({
        turn: state.turn,
        clock_h: state.clock_h,
        famille: abusive ? "buzz" : "quotidien",
        text: abusive
            ? "Tu annonces une pause. Encore une. Les commentaires ne sont pas dupes."
            : "Tu annonces une pause à la communauté. Ils comprennent — repose-toi.",
    });
}
/**
 * ⟦A-2 point 5b · A-104⟧ Programme l'échéance d'un événement de contenu au **prochain**
 * tirage (`evenementsDus`, `effects.ts`), sans consommer de `draw` — le même mécanisme que
 * l'effet `event_schedule`, à la donnée près.
 *
 * **Pourquoi ce n'est pas un appel à `applyEffectsInPlace`** : `effects.ts` importe déjà
 * `pause.js` (transitivement, via `actions.ts` → `cloturerPauseAuRetour`/`bonusRetourEnForme`)
 * — `pause.ts` important `effects.ts` en retour fermerait un cycle. La forme posée est
 * strictement celle d'un `ScheduledEvent` (§10.5) : `evenementsDus` la consomme sans savoir
 * qui l'a écrite, exactement comme pour un `event_schedule` de contenu.
 */
function programmerEvenementSysteme(state, eventId, dueH) {
    state.content_effects ??= { modifiers: [], flags: {}, scheduled_events: [] };
    state.content_effects.scheduled_events.push({ event_id: eventId, due_h: dueH });
}
/**
 * Au sommeil : transforme l'intention en pause effective (point 4). MUTE `state`.
 *
 * C'est ici que se joue le point 5b : hors de la fenêtre des 90 jours, la pause est
 * **abusive** — `protege: false` (aucun gel du silence, `sleep.ts` le lit), image −10 vers le
 * chaos, Fidélité −5. À la 3e abusive PRÉCISÉMENT (pas la 4e, pas la 5e), le bad buzz dédié
 * `EVENEMENT_PAUSE_ABUSIVE` est désormais **programmé**, pas seulement signalé — ⟦A-104⟧ ferme
 * le trou qu'⟦A-2⟧ laissait « pour le contenu M3 ».
 */
export function demarrerPauseAuSommeil(state, events = [], log = [], 
/**
 * ⟦SPEC-FIL §2.5⟧ Le contenu, pour lire l'interrupteur du Fil. **Absent ⇒ Fil éteint**, donc
 * le comportement d'avant : les appelants de test qui montent la file à la main n'ont rien
 * à changer, et l'omission ne peut jamais faire apparaître un effet de Hype par surprise.
 */
content) {
    if (state.counters.pause_intention_h === null || state.counters.pause_intention_h === undefined) {
        return false;
    }
    if (state.pause_announced)
        return false; // déjà en pause : l'intention se périme
    const abusive = prochainePauseAbusive(state);
    state.pause_announced = { since_h: state.clock_h, protege: !abusive };
    state.counters.pause_intention_h = null;
    state.counters.pauses_annoncees = pausesAnnoncees(state) + 1;
    if (abusive) {
        state.counters.pauses_abusives = pausesAbusives(state) + 1;
        // « Faire semblant d'avoir des problèmes EST un contenu chaos » (A-2 point 5b, §4.7).
        state.channel_image_offset = (state.channel_image_offset ?? 0) + ABUS_IMAGE;
        state.loyalty += ABUS_FIDELITE;
        // ⟦SPEC-FIL §2.5⟧ Le seul des trois qui s'AJOUTE au lieu de remplacer : l'avenant écrit
        // « rien à retirer — les malus image/Fidélité restent », et « Hype −10 **en plus** ».
        // À Fil éteint, `gagnerHype` rend 0 sans rien poser : l'état neutre, littéralement.
        const hypeAbus = content ? gagnerHype(state, ABUS_HYPE, content) : 0;
        // ⟦A-104⟧ Précisément à la 3e — ni avant (rien à programmer), ni après (déjà programmée,
        // `evenementsDus` la consommera à la première occasion et il ne faut pas l'y remettre).
        if (pausesAbusives(state) === ABUS_SEUIL_BAD_BUZZ) {
            programmerEvenementSysteme(state, EVENEMENT_PAUSE_ABUSIVE, state.clock_h);
        }
        events.push({
            kind: "warning",
            data: {
                cause: "pause_abusive",
                abusives: pausesAbusives(state),
                image_delta: ABUS_IMAGE,
                fidelite_delta: ABUS_FIDELITE,
                // Fil éteint ⇒ 0, et la clé porte exactement ce qui a été appliqué ⟦A-16⟧.
                hype_delta: hypeAbus,
                // ⟦A-104⟧ Le bad buzz « Il se moque de nous » n'est plus seulement signalé : à la 3e
                // pause abusive, il est désormais PROGRAMMÉ (voir `programmerEvenementSysteme`
                // ci-dessus). Le champ reste : c'est ce que `>= ABUS_SEUIL_BAD_BUZZ` a toujours voulu
                // dire — vrai à la 3e et à toutes les suivantes, même si une seule programme l'événement.
                bad_buzz_du: pausesAbusives(state) >= ABUS_SEUIL_BAD_BUZZ,
            },
        });
        log.push({
            turn: state.turn,
            clock_h: state.clock_h,
            famille: "buzz",
            text: "Tu repars en pause. Un thread compile toutes tes pauses. Ça ne passe plus.",
        });
    }
    else {
        events.push({ kind: "warning", data: { cause: "pause_demarree", protege: true } });
    }
    return true;
}
/**
 * À la publication : **clôt** la pause en cours et rend le bonus de retour à appliquer sur
 * VuesPic. C'est la « première vidéo publiée après la pause » du point 3, et c'est la SEULE
 * qui en profite (point 5c) — puisque la pause est refermée ici.
 *
 * MUTE `state`. Rend `null` si aucune pause n'était en cours.
 */
export function cloturerPauseAuRetour(state, events = [], 
/** ⟦SPEC-FIL §2.5⟧ Voir `demarrerPauseAuSommeil` : **absent ⇒ Fil éteint ⇒ transitoire**. */
content) {
    const pause = state.pause_announced;
    if (!pause)
        return null;
    const jours = joursDePause(state);
    const mult = multiplicateurRetour(state);
    const tropCourte = pause.protege && jours < H_PAUSE_MIN_BONUS / H_JOUR;
    // 🔑 L'aiguillage des transitoires ⟦A-2 → SPEC-FIL §2.5⟧, et il n'y en a qu'un.
    const surLaHype = content !== undefined && filActif(content);
    let hypeDelta = 0;
    if (mult > 1) {
        // Une pause bonifiée ferme la fenêtre des 90 jours (point 5a).
        state.counters.last_pause_bonifiee_h = state.clock_h;
        if (surLaHype) {
            hypeDelta = gagnerHype(state, RETOUR_HYPE, content);
        }
        else {
            // ⟦A-4 §2 / A-26⟧ Gain de Fidélité ⇒ trait de genre. Le `ABUS_FIDELITE` plus haut est une
            // PERTE et reste intact : un ×1,2 sur une perte punirait le côté qu'il avantage (règle 1).
            state.loyalty += appliquerTraitJauge(state.genre, "loyalty", RETOUR_FIDELITE);
            state.channel_image_offset = (state.channel_image_offset ?? 0) + RETOUR_IMAGE;
        }
    }
    else if (tropCourte) {
        if (surLaHype) {
            hypeDelta = gagnerHype(state, PAUSE_COURTE_HYPE, content);
        }
        else {
            // « Moqueries » du point 5b, re-mappées sur l'image.
            state.channel_image_offset = (state.channel_image_offset ?? 0) + PAUSE_COURTE_IMAGE;
        }
    }
    state.pause_announced = null;
    events.push({
        kind: "warning",
        data: {
            cause: "retour_de_pause",
            jours: Math.round(jours * 10) / 10,
            mult: Math.round(mult * 1000) / 1000,
            protegee: pause.protege,
            trop_courte: tropCourte,
            hype_delta: hypeDelta,
        },
    });
    return { mult, jours, protegee: pause.protege, trop_courte: tropCourte };
}
/**
 * Complément du bonus de retour qui dépend de la vidéo elle-même : « il est revenu en forme »
 * (point 3), Fidélité +5 si la Qualité de la vidéo de retour est ≥ 1,2. Appelé par le pipeline
 * de publication une fois `qualite_eff` connue. MUTE `state`.
 */
export function bonusRetourEnForme(state, retour, qualiteEff) {
    if (!retour || retour.mult <= 1)
        return false;
    if (qualiteEff < SEUIL_QUALITE_EN_FORME)
        return false;
    state.loyalty += appliquerTraitJauge(state.genre, "loyalty", RETOUR_FIDELITE_EN_FORME);
    return true;
}
//# sourceMappingURL=pause.js.map